// Copied from platform-app components/pos/customerFacing/CustomerDisplayPreview.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { APosCustomerDisplayConfig } from '@platform/shared-types';
import IllustrativePreview from './IllustrativePreview';
import { currentPosPreviewOrigin } from './posPreviewOrigin';

/**
 * Live preview of the customer display — the REAL screens.
 *
 * Frames the till's `#customer-display-preview` route (AGENTIC_POS, Task D7) and
 * drives it over postMessage. Contract (must match the POS exactly):
 *   parent → iframe  { type: 'CFD_PREVIEW', config, theme, screen }
 *   iframe → parent  { type: 'CFD_PREVIEW_READY' }   (on mount)
 * `config` is the nested customerDisplayConfig object, unchanged.
 *
 * Security: READY is accepted only from the POS origin AND from this iframe's
 * own window; every post names the POS origin as targetOrigin (never '*').
 *
 * If the till never says READY within READY_TIMEOUT_MS (offline, blocked, or no
 * till for this host) the hand-drawn IllustrativePreview is shown so the tab is
 * never blank. The iframe stays mounted (hidden) so a late READY still wins.
 */

export type PreviewScreen = 'idle' | 'cart' | 'confirm' | 'tip' | 'receipt' | 'thankyou' | 'refund';
export type PreviewTheme = 'light' | 'dark';

export const READY_TIMEOUT_MS = 4000;
export const PREVIEW_ROUTE = '#customer-display-preview';

/** Virtual screen the till renders at; scaled down to fit the panel. 16:9. */
const VIRTUAL_W = 1280;
const VIRTUAL_H = 720;

interface Props {
  config: APosCustomerDisplayConfig;
  screen: PreviewScreen;
  theme: PreviewTheme;
  /** Override for tests; defaults to the origin derived from the current host. */
  posOrigin?: string | null;
}

export default function CustomerDisplayPreview({ config, screen, theme, posOrigin: posOriginProp }: Props) {
  const posOrigin = posOriginProp === undefined ? currentPosPreviewOrigin() : posOriginProp;
  if (!posOrigin) return <IllustrativePreview config={{ ...config, theme }} screen={screen === 'thankyou' ? 'thankyou' : 'idle'} />;
  // key = origin: a different till origin is a different frame, so remount and
  // start clean — frame-live gate, READY and the 4000ms fallback window are all
  // per-frame, and nothing from the old frame (incl. a late READY) carries over.
  // (The origin is fixed per page load in practice; this is for robustness.)
  // (Key on a Fragment: this app's React typings reject `key` on typed function components.)
  return (
    <React.Fragment key={posOrigin}>
      <RealScreenPreview config={config} screen={screen} theme={theme} posOrigin={posOrigin} />
    </React.Fragment>
  );
}

function RealScreenPreview({ config, screen, theme, posOrigin }: Omit<Props, 'posOrigin'> & { posOrigin: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [scale, setScale] = useState(420 / VIRTUAL_W);

  // Latest payload for the message listener / onLoad without re-subscribing.
  const latest = useRef({ config, screen, theme });
  latest.current = { config, screen, theme };

  // True once the frame has loaded or said READY. Before that the iframe is still
  // about:blank and the browser would drop the post ("target origin does not match").
  const frameLiveRef = useRef(false);

  const post = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win || !frameLiveRef.current) return;
    const { config: c, screen: s, theme: t } = latest.current;
    win.postMessage({ type: 'CFD_PREVIEW', config: c, theme: t, screen: s }, posOrigin);
  }, [posOrigin]);

  // READY handshake.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== posOrigin) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      if (!event.data || typeof event.data !== 'object' || event.data.type !== 'CFD_PREVIEW_READY') return;
      frameLiveRef.current = true;
      setReady(true);
      post();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [posOrigin, post]);

  // Fallback if the till never answers.
  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => setTimedOut(true), READY_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [posOrigin, ready]);

  // Re-post whenever what is being previewed changes.
  useEffect(() => { post(); }, [config, screen, theme, post]);

  // Scale the virtual 16:9 screen to the panel width.
  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const fit = () => { if (el.clientWidth > 0) setScale(el.clientWidth / VIRTUAL_W); };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [posOrigin]);

  const illustrative = (
    <IllustrativePreview
      config={{ ...config, theme }}
      screen={screen === 'thankyou' ? 'thankyou' : 'idle'}
    />
  );

  const showFallback = timedOut && !ready;

  return (
    <>
      {showFallback && illustrative}
      <div
        ref={boxRef}
        hidden={showFallback}
        data-testid="cfp-real-preview"
        style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', background: theme === 'dark' ? '#0f0f0f' : '#f4f4f5' }}
      >
        <iframe
          ref={iframeRef}
          title="Customer display preview"
          src={`${posOrigin}/${PREVIEW_ROUTE}`}
          sandbox="allow-scripts allow-same-origin"
          onLoad={() => { frameLiveRef.current = true; post(); }}
          tabIndex={-1}
          style={{
            pointerEvents: 'none',
            position: 'absolute', left: 0, top: 0,
            width: VIRTUAL_W, height: VIRTUAL_H, border: 0,
            transform: `scale(${scale})`, transformOrigin: '0 0',
          }}
        />
      </div>
    </>
  );
}
